import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, ExternalLink, Trash2, Download, Loader2, RefreshCw, Search } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";

function emptyTrip() {
  return {
    reference: "",
    external_url: "",
    client_name: "",
    client_email: "",
    client_phone: "",
    adults: 2,
    children: 0,
    arrival_date: "",
    departure_date: "",
    flight_number: "",
    notes: "",
  };
}

// Extrae la referencia (último número) desde una URL del estilo
// https://gestion.viajadverdad.com/trips/form/1/84  →  "84"
function extractRefFromUrl(url) {
  if (!url) return "";
  const m = url.match(/\/(\d+)(?:[\/?#]|$)/g);
  if (!m || m.length === 0) return "";
  const last = m[m.length - 1];
  return last.replace(/[^\d]/g, "");
}

// Resumen semáforo del itinerario: "2🔴 5🟡 23🟢" (solo muestra los colores con reservas)
const LIGHT_DOTS = [
  ["red", "bg-red-500", "con discrepancia — revisar respuesta"],
  ["amber", "bg-amber-400", "esperando respuesta / por revisar"],
  ["green", "bg-emerald-500", "confirmadas"],
  ["gray", "bg-gray-300", "sin contactar"],
];

function TripLights({ counts, reference }) {
  if (!counts) return <span className="text-gray-300 text-xs">—</span>;
  return (
    <div className="flex items-center gap-2.5" data-testid={`trip-lights-${reference}`}>
      {LIGHT_DOTS.map(([k, cls, label]) =>
        counts[k] > 0 ? (
          <span key={k} className="inline-flex items-center gap-1 text-xs text-gray-600"
            title={`${counts[k]} reserva${counts[k] > 1 ? "s" : ""} ${label}`}>
            <span className={`inline-block w-2 h-2 rounded-full ${cls}`} />
            {counts[k]}
          </span>
        ) : null
      )}
    </div>
  );
}

export default function TripsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [agents, setAgents] = useState([]);
  // Filtros persistidos en localStorage para mantenerlos al volver de un itinerario
  const [scope, setScope] = useState(() => localStorage.getItem("vv:trips:scope") || "mine"); // mine | all
  const [agentId, setAgentId] = useState(() => localStorage.getItem("vv:trips:agentId") || "");
  const [q, setQ] = useState(() => localStorage.getItem("vv:trips:q") || "");
  const [open, setOpen] = useState(false);
  const [openImport, setOpenImport] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const [importing, setImporting] = useState(false);
  const [form, setForm] = useState(emptyTrip());
  const [loading, setLoading] = useState(false);
  const [lights, setLights] = useState({});

  // Persistir filtros al cambiar
  useEffect(() => { localStorage.setItem("vv:trips:scope", scope); }, [scope]);
  useEffect(() => { localStorage.setItem("vv:trips:agentId", agentId); }, [agentId]);
  useEffect(() => { localStorage.setItem("vv:trips:q", q); }, [q]);

  const fetchLights = async () => {
    try {
      const { data } = await api.get("/trips/lights");
      setLights(data);
    } catch {
      /* silencioso: el semáforo es informativo */
    }
  };

  const fetchAll = async () => {
    const params = {};
    if (scope === "all" && !agentId) params.mine = false;
    else if (agentId) { params.agent_id = agentId; params.mine = false; }
    else params.mine = true;
    if (q) params.q = q;
    const { data } = await api.get("/trips", { params });
    setItems(data);
  };

  useEffect(() => { fetchAll(); }, [scope, agentId, q]); // eslint-disable-line

  useEffect(() => { fetchLights(); }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/agents");
        setAgents(data);
      } catch {
        /* silencioso */
      }
    })();
  }, []);

  const onImport = async (e) => {
    e?.preventDefault?.();
    if (!importUrl) return;
    setImporting(true);
    try {
      const { data } = await api.post("/trips/import", { url: importUrl });
      toast.success(`Itinerario ${data.reference} importado · ${data.bookings_imported} reservas`);
      setOpenImport(false);
      setImportUrl("");
      fetchAll();
      fetchLights();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al importar");
    } finally {
      setImporting(false);
    }
  };

  const onReimport = async (trip) => {
    if (!trip.external_url) {
      toast.error("Este itinerario no tiene URL de gestion para reimportar");
      return;
    }
    if (!confirm(`¿Volver a importar ${trip.reference}? Se sobreescribirán datos y reservas.`)) return;
    try {
      const { data } = await api.post("/trips/import", { url: trip.external_url });
      toast.success(`Reimportado · ${data.bookings_imported} reservas`);
      fetchAll();
      fetchLights();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al reimportar");
    }
  };

  const onCreate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, adults: Number(form.adults), children: Number(form.children) };
      await api.post("/trips", payload);
      toast.success("Itinerario creado");
      setOpen(false);
      setForm(emptyTrip());
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  const onDelete = async (id) => {
    if (!confirm("¿Borrar este itinerario y sus reservas?")) return;
    await api.delete(`/trips/${id}`);
    fetchAll();
  };

  return (
    <div className="space-y-6" data-testid="trips-page">
      <div className="flex items-center justify-between">
        <div>
          <p className="vv-label">Operaciones</p>
          <h1 className="text-3xl font-semibold tracking-tight mt-1">Itinerarios</h1>
          <p className="text-sm text-gray-500 mt-1">Crea un itinerario y carga las reservas día por día.</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={openImport} onOpenChange={setOpenImport}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700" data-testid="import-trip-button">
                <Download size={16} className="mr-1" /> Importar desde gestion
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Importar itinerario</DialogTitle>
              </DialogHeader>
              <form onSubmit={onImport} className="space-y-4">
                <div>
                  <Label className="vv-label">URL del itinerario</Label>
                  <Input
                    autoFocus
                    required
                    value={importUrl}
                    onChange={(e) => setImportUrl(e.target.value)}
                    placeholder="https://gestion.viajadverdad.com/trips/form/1/84"
                    data-testid="import-url-input"
                  />
                  <p className="text-[11px] text-gray-500 mt-2 leading-relaxed">
                    Pega la URL completa del itinerario en gestion.viajadverdad.com. Iniciaremos sesión con la cuenta corporativa y traeremos automáticamente los datos del cliente, fechas, vuelo y todas las líneas de reserva. Los proveedores se enlazarán por nombre (los nuevos se crearán con sistema MAIL por defecto).
                  </p>
                </div>
                <DialogFooter>
                  <Button type="button" variant="ghost" onClick={() => setOpenImport(false)} disabled={importing}>Cancelar</Button>
                  <Button type="submit" disabled={importing || !importUrl} className="bg-blue-600 hover:bg-blue-700" data-testid="import-confirm-button">
                    {importing ? <Loader2 size={14} className="mr-2 animate-spin"/> : <Download size={14} className="mr-2"/>}
                    {importing ? "Importando…" : "Importar"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="create-trip-button">
                <Plus size={16} className="mr-1" /> Crear manual
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Nuevo itinerario</DialogTitle>
            </DialogHeader>
            <form onSubmit={onCreate} className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="vv-label">URL del itinerario (gestion.viajadverdad.com)</Label>
                <Input
                  value={form.external_url}
                  onChange={(e) => {
                    const url = e.target.value;
                    const ref = extractRefFromUrl(url);
                    setForm({ ...form, external_url: url, reference: form.reference || ref });
                  }}
                  data-testid="trip-url-input"
                  placeholder="https://gestion.viajadverdad.com/trips/form/1/84"
                />
                <p className="text-[11px] text-gray-500 mt-1">Pega la URL completa. La referencia se rellenará sola con el número final.</p>
              </div>
              <div className="col-span-1">
                <Label className="vv-label">Referencia *</Label>
                <Input required value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} data-testid="trip-reference-input" placeholder="84" />
              </div>
              <div className="col-span-1">
                <Label className="vv-label">Cliente</Label>
                <Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} data-testid="trip-client-input" placeholder="(se rellenará al importar)" />
              </div>
              <div className="col-span-1">
                <Label className="vv-label">Email cliente</Label>
                <Input type="email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} />
              </div>
              <div className="col-span-1">
                <Label className="vv-label">Teléfono cliente</Label>
                <Input value={form.client_phone} onChange={(e) => setForm({ ...form, client_phone: e.target.value })} />
              </div>
              <div className="col-span-1">
                <Label className="vv-label">Adultos</Label>
                <Input type="number" min={0} value={form.adults} onChange={(e) => setForm({ ...form, adults: e.target.value })} />
              </div>
              <div className="col-span-1">
                <Label className="vv-label">Niños</Label>
                <Input type="number" min={0} value={form.children} onChange={(e) => setForm({ ...form, children: e.target.value })} />
              </div>
              <div className="col-span-1">
                <Label className="vv-label">Fecha llegada</Label>
                <Input type="date" value={form.arrival_date} onChange={(e) => setForm({ ...form, arrival_date: e.target.value })} />
              </div>
              <div className="col-span-1">
                <Label className="vv-label">Fecha salida</Label>
                <Input type="date" value={form.departure_date} onChange={(e) => setForm({ ...form, departure_date: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Label className="vv-label">Vuelo de llegada</Label>
                <Input value={form.flight_number} onChange={(e) => setForm({ ...form, flight_number: e.target.value })} placeholder="IB 2345" />
              </div>
              <DialogFooter className="col-span-2 mt-2">
                <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
                <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700" data-testid="save-trip-button">
                  {loading ? "Guardando…" : "Crear itinerario"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="vv-card p-3 flex items-center gap-3 flex-wrap" data-testid="trips-filters">
        <div className="inline-flex rounded-md border border-gray-200 overflow-hidden">
          <button
            onClick={() => { setScope("mine"); setAgentId(""); }}
            className={`px-3 py-1.5 text-xs font-medium ${scope === "mine" && !agentId ? "bg-blue-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
            data-testid="filter-mine-button"
          >
            Mis itinerarios
          </button>
          <button
            onClick={() => { setScope("all"); setAgentId(""); }}
            className={`px-3 py-1.5 text-xs font-medium border-l border-gray-200 ${scope === "all" && !agentId ? "bg-blue-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
            data-testid="filter-all-button"
          >
            Todos
          </button>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] uppercase tracking-wider text-gray-500">Agente</span>
          <Select value={agentId || "any"} onValueChange={(v) => { setAgentId(v === "any" ? "" : v); if (v !== "any") setScope("all"); }}>
            <SelectTrigger className="w-[180px] h-9" data-testid="filter-agent-select"><SelectValue placeholder="Cualquiera"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Cualquiera</SelectItem>
              {agents.map((a) => (
                <SelectItem key={a.id} value={a.id}>{a.name} {a.id === user?.id ? "(yo)" : ""}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por cliente o referencia…" className="pl-9 h-9" data-testid="trips-search-input"/>
        </div>
        <span className="text-xs text-gray-500">{items.length} itinerarios</span>
      </div>

      <div className="vv-card overflow-hidden">
        {items.length === 0 ? (
          <div className="p-12 text-center text-sm text-gray-500">
            Aún no hay itinerarios. Crea el primero para empezar.
          </div>
        ) : (
          <table className="vv-table w-full">
            <thead>
              <tr>
                <th>Ref</th>
                <th>Cliente</th>
                <th>Llegada</th>
                <th>Salida</th>
                <th>Pax</th>
                <th>Vuelo</th>
                <th title="Semáforo de reservas: rojo = discrepancia · ámbar = esperando/por revisar · verde = confirmada · gris = sin contactar">Reservas</th>
                <th>Agente</th>
                <th>Estado</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((t) => (
                <tr key={t.id}>
                  <td className="mono text-xs">{t.reference}</td>
                  <td className="font-medium">
                    <Link to={`/itinerarios/${t.id}`} className="hover:text-blue-700" data-testid={`open-trip-${t.reference}`}>
                      {t.client_name || <span className="text-gray-400 italic">Pendiente de importar</span>}
                    </Link>
                  </td>
                  <td>{t.arrival_date || "—"}</td>
                  <td>{t.departure_date || "—"}</td>
                  <td>{t.adults}A {t.children ? `+${t.children}N` : ""}</td>
                  <td>{t.flight_number || "—"}</td>
                  <td><TripLights counts={lights[t.id]} reference={t.reference} /></td>
                  <td className="text-xs text-gray-500">{(agents.find((a) => a.id === (t.assigned_to || t.created_by))?.name) || "—"}</td>
                  <td>
                    <span className="inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-gray-100 text-gray-700">{t.status}</span>
                  </td>
                  <td className="text-right whitespace-nowrap">
                    {t.external_url && (
                      <>
                        <button onClick={() => onReimport(t)} className="text-gray-400 hover:text-blue-700 inline-flex mr-3" title="Reimportar desde gestion" data-testid={`reimport-trip-${t.reference}`}>
                          <RefreshCw size={14} />
                        </button>
                        <a href={t.external_url} target="_blank" rel="noreferrer" className="text-gray-500 hover:text-gray-800 inline-flex items-center mr-3">
                          <ExternalLink size={14} />
                        </a>
                      </>
                    )}
                    <Link to={`/itinerarios/${t.id}`} className="text-xs text-blue-600 hover:underline mr-3">Abrir</Link>
                    <button onClick={() => onDelete(t.id)} className="text-gray-400 hover:text-red-600 inline-flex" title="Borrar">
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
